import React, { FC, MutableRefObject, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Form as RsuiteForm, Heading, VStack } from "rsuite";
import { FormInstance } from "rsuite/esm/Form/hooks/useFormRef";
import { FormProps } from "rsuite/esm/Form/Form";

import {
    Features,
    OcrConfiguration,
    OcrMethods,
    TenantFeatureGroup,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";

import { ocrMethods } from "../../Mailboxes/mailboxes-helpers";

import CheckboxGroup, {
    Props as CheckboxGroupProps,
} from "components/Environments/Tenants/LeftMenu/FeatureConfigurations/Form/Groups/Checkbox/Checkbox";

import DropdownGroup, {
    Props as DropdownGroupProps,
} from "components/Environments/Tenants/LeftMenu/FeatureConfigurations/Form/Groups/Dropdown/Dropdown";

interface Props extends FormProps {
    formRef: MutableRefObject<FormInstance<Record<string, any>> | null>;
    containerRef: RefObject<HTMLDivElement>;

    settings: Features;
    ocrConfigurations: OcrConfiguration;
}

const Form: FC<Props> = ({
    formRef,
    containerRef,
    settings,
    ocrConfigurations,
    disabled,
    onChange,
    ...props
}: Props) => {
    const { t } = useTranslation();

    const checkboxFeatures: CheckboxGroupProps[] = [
        {
            value: "vectorVendorRecognitionEnabled",
            checked: settings?.vectorVendorRecognitionEnabled,
            disabled: disabled,
            helpText: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionEnabled.description"
            ),
            title: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionEnabled.title"
            ),
            group: TenantFeatureGroup.vendorSimilaritySearch,
            onChange: (value, checked) =>
                handleChange(value as keyof Features, checked),
        },
    ];

    const dropdownFeatures: DropdownGroupProps[] = [
        {
            value: ocrConfigurations.ocrMethod,
            name: "ocrMethod",
            disabled: disabled,
            cleanable: false,
            helpText: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.description"
            ),
            data: ocrMethods,
            title: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.title"
            ),
            placeholder: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.placeholder"
            ),
            searchable: false,
            container: () => containerRef.current!,
            group: TenantFeatureGroup.ocr,
            onChange: (value) =>
                handleChange("ocrMethod", value as keyof OcrConfiguration),
        },
    ];

    const groups = Object.entries(TenantFeatureGroup)
        .map(([key, value]) => {
            const checkboxGroups = checkboxFeatures
                .filter((feature) => feature.group === value)
                .map((feature, index) => (
                    <CheckboxGroup key={index} {...feature} />
                ));

            const dropdownGroups = dropdownFeatures
                .filter((feature) => feature.group === value)
                .map((feature, index) => (
                    <DropdownGroup key={index} {...feature} />
                ));

            if (checkboxGroups.length === 0 && dropdownGroups.length === 0) {
                return null;
            }

            const title = t(`tenantConfig.featureConfigurations.group.${key}`);

            return (
                <>
                    <Heading level={3}>{title}</Heading>
                    {checkboxGroups}
                    {dropdownGroups}
                </>
            );
        })
        .filter((group) => group !== null);

    const handleChange = (
        name: keyof Features | keyof OcrConfiguration,
        value: string | number | boolean | OcrMethods
    ) => {
        onChange?.({ ...settings, [name]: value });
    };

    return (
        <RsuiteForm {...props} ref={formRef} formValue={settings}>
            <VStack spacing={5} justifyContent={"flex-start"}>
                {groups.map((group, index) => (
                    <VStack.Item key={index} alignSelf={"flex-start"}>
                        {group}
                    </VStack.Item>
                ))}
            </VStack>
        </RsuiteForm>
    );
};

export default Form;
